import { XMLBuilder, XMLParser, XMLValidator } from "fast-xml-parser";
import type { MetaArgs } from "react-router";

export type HreflangAlternate = {
  hreflang: string;
  href: string;
};

export type HreflangMap = Map<string, Array<HreflangAlternate>>;

type RootLoaderData = {
  hreflangAlternates: Array<HreflangAlternate>;
  hreflangIsAuthoritative?: boolean;
};

export type RouteMetaMatch = MetaArgs["matches"][number];

type SitemapLink = { "@_hreflang"?: string; "@_href"?: string };
type SitemapUrl = {
  loc?: string;
  "xhtml:link"?: Array<SitemapLink>;
};
type SitemapXml = {
  urlset?: { url?: Array<SitemapUrl>; "@_xmlns:xhtml"?: string };
};
type InflightMap = { key: string; promise: Promise<HreflangMap> };

const SOURCE_CACHE_MS = 60_000;

const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: "@_",
  parseTagValue: false,
  isArray: (name) => name === "url" || name === "xhtml:link",
});

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isHreflangAlternate(value: unknown): value is HreflangAlternate {
  return (
    isObject(value) &&
    typeof value.hreflang === "string" &&
    typeof value.href === "string"
  );
}

function isRootLoaderData(value: unknown): value is RootLoaderData {
  return (
    isObject(value) &&
    Array.isArray(value.hreflangAlternates) &&
    value.hreflangAlternates.every(isHreflangAlternate)
  );
}

function pathWithoutLocale(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  const isLang = /^[a-z]{2}(-[a-zA-Z]{2})?$/.test(first ?? "");
  const remainder = isLang ? segments.slice(1) : segments;

  return remainder.length > 0 ? `/${remainder.join("/")}` : "/";
}

// Strip leading "/{lang}" or "/{lang}-{country}" from a URL pathname.
// Must match neighbour-puller's `canonicalPathFromUrl` semantics in CMS extension.
export function canonicalPath(
  rawUrl: string,
  expectedHost: string | null,
): string | null {
  try {
    const url = new URL(rawUrl);
    return expectedHost === null || url.host === expectedHost
      ? pathWithoutLocale(url.pathname)
      : null;
  } catch {
    return null;
  }
}

export function normalisePath(pathname: string): string {
  return pathWithoutLocale(pathname);
}

function parseSitemap(xml: string): SitemapXml {
  if (XMLValidator.validate(xml) !== true) {
    throw new Error("Invalid hreflang sitemap XML");
  }
  const sitemap = xmlParser.parse(xml) as SitemapXml;
  if (!isObject(sitemap) || !("urlset" in sitemap)) {
    throw new Error("Hreflang source is not a sitemap urlset");
  }
  return sitemap;
}

function parseXml(xml: string, selfHost: string | null): HreflangMap {
  const sitemap = parseSitemap(xml);
  const byPath = new Map<string, Map<string, string>>();
  for (const url of sitemap.urlset?.url ?? []) {
    const path = url.loc ? canonicalPath(url.loc, selfHost) : null;
    if (path === null) continue;

    const links = byPath.get(path) ?? new Map<string, string>();
    byPath.set(path, links);

    for (const link of url["xhtml:link"] ?? []) {
      if (link["@_hreflang"] && link["@_href"]) {
        links.set(link["@_hreflang"], link["@_href"]);
      }
    }
  }

  const result: HreflangMap = new Map();
  for (const [path, links] of byPath) {
    result.set(
      path,
      Array.from(links.entries()).map(([hreflang, href]) => ({
        hreflang,
        href,
      })),
    );
  }
  return result;
}

export function applyHreflangToSitemap(xml: string, map: HreflangMap): string {
  const sitemap = parseSitemap(xml);
  if (!sitemap.urlset) return xml;

  sitemap.urlset["@_xmlns:xhtml"] = "http://www.w3.org/1999/xhtml";
  for (const url of sitemap.urlset.url ?? []) {
    const path = url.loc ? canonicalPath(url.loc, null) : null;
    const alternates = path === null ? [] : (map.get(path) ?? []);
    delete url["xhtml:link"];
    if (alternates.length > 0) {
      url["xhtml:link"] = alternates.map(({ hreflang, href }) => ({
        "@_rel": "alternate",
        "@_hreflang": hreflang,
        "@_href": href,
      }));
    }
  }

  return new XMLBuilder({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    format: true,
    suppressEmptyNode: true,
  }).build(sitemap);
}

type GetHreflangMapParams = {
  cmsPublicPath: string;
  sitemapUuid: string | null;
  selfHost: string;
  sourceSitemapUrl?: string | null;
};

class HreflangMapLoader {
  private key: string | null = null;
  private expiresAt = 0;
  private map: HreflangMap = new Map();
  private inflight: InflightMap | null = null;

  public get(params: GetHreflangMapParams): Promise<HreflangMap> {
    const source = params.sourceSitemapUrl?.trim();
    if (!source && !params.sitemapUuid) return Promise.resolve(new Map());
    const url = source?.length
      ? source
      : `${params.cmsPublicPath.replace(/\/$/, "")}/assets/${params.sitemapUuid}`;
    const key = source ? `source:${url}` : `local:${url}:${params.selfHost}`;
    if (this.key === key && Date.now() < this.expiresAt) {
      return Promise.resolve(this.map);
    }
    if (this.inflight?.key === key) return this.inflight.promise;

    const promise = this.load(
      url,
      key,
      params.selfHost,
      Boolean(source),
    ).finally(() => {
      if (this.inflight?.key === key) this.inflight = null;
    });
    this.inflight = { key, promise };

    return promise;
  }

  private async load(
    url: string,
    key: string,
    selfHost: string,
    inherited: boolean,
  ): Promise<HreflangMap> {
    try {
      const source = new URL(url);
      if (
        !["https:", "http:"].includes(source.protocol) ||
        source.username ||
        source.password
      ) {
        throw new Error("Invalid hreflang source URL");
      }
      const response = await fetch(url, {
        signal: AbortSignal.timeout(10_000),
        cache: inherited ? "no-cache" : "default",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const xml = await response.text();
      const map = parseXml(xml, inherited ? null : selfHost);
      this.key = key;
      this.map = map;
      this.expiresAt = inherited ? Date.now() + SOURCE_CACHE_MS : Infinity;
      return map;
    } catch (error) {
      console.warn(
        "[hreflang] Sitemap refresh failed (%s)",
        error instanceof Error && /^HTTP \d+$/.test(error.message)
          ? error.message
          : "unavailable or invalid sitemap",
      );
      if (inherited) {
        const previous =
          this.key === key
            ? this.map
            : new Map<string, Array<HreflangAlternate>>();
        this.key = key;
        this.map = previous;
        this.expiresAt = Date.now() + SOURCE_CACHE_MS;
        return previous;
      }
      return new Map();
    }
  }
}

const hreflangMapLoader = new HreflangMapLoader();

export function getHreflangSourceConfig(domains: unknown): {
  sourceSitemapUrl: string | null;
  authoritative: boolean;
} {
  const sourceSitemapUrl =
    isObject(domains) && typeof domains.source_sitemap_url === "string"
      ? domains.source_sitemap_url.trim() || null
      : null;
  return {
    sourceSitemapUrl,
    authoritative:
      Boolean(sourceSitemapUrl) ||
      (isObject(domains) &&
        Array.isArray(domains.publications) &&
        domains.publications.length > 0),
  };
}

export function usesAuthoritativeHreflang(
  matches: Array<RouteMetaMatch>,
): boolean {
  const root = matches.find((match) => match.id === "root");
  const data = root?.loaderData ?? root?.data;
  return isRootLoaderData(data) && Boolean(data.hreflangIsAuthoritative);
}

export function getHreflangMap(
  params: GetHreflangMapParams,
): Promise<HreflangMap> {
  return hreflangMapLoader.get(params);
}

export function getRootHreflangAlternates(
  matches: Array<RouteMetaMatch>,
): Array<HreflangAlternate> {
  const root = matches.find((match) => match.id === "root");
  const data = root?.loaderData ?? root?.data;

  return isRootLoaderData(data) ? data.hreflangAlternates : [];
}

export function lookupAlternates(
  map: HreflangMap,
  pathname: string,
): Array<HreflangAlternate> {
  return map.get(normalisePath(pathname)) ?? [];
}
